import { Component, NgModuleRef } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, from, Observable, of } from "rxjs";
import { flatMap, map, mergeMap, switchMap, tap, toArray } from "rxjs/operators";
import { Reference } from "src/app/inventory/reference/reference";
import { ReferenceService } from "src/app/inventory/reference/reference.service";
import { ReferenceSearchDialogComponent } from "src/app/inventory/reference/search/reference-search-dialog.component";
import { NeedaConfig } from "src/app/inventory/supplier/needa-config";
import { SupplierService } from "src/app/inventory/supplier/supplier.service";
import { Page } from "src/app/util/page";
import { UpdateSupplierReferenceDialogComponent } from "../../reference/update-supplier-reference-dialog/update-supplier-reference-dialog.component";
import { Rma } from "../rma";
import { RmaDetail } from "../rma-detail";
import { RmaService } from "../rma-service";

@Component({
  styleUrls: ['./rma-details.component.css'],
  templateUrl: './rma-details.component.html'
})
export class RmaDetailsComponent {
  public query = "";
  public needaConfig!: NeedaConfig;
  public needaConfigMissing = false;
  public rma!: Rma;

  public loading = false;
  public searchInProgress = false;

  constructor(
    private modalService: NgbModal,
    private rmaService: RmaService,
    private referenceService: ReferenceService,
    private router: Router,
    activatedRoute: ActivatedRoute,
  ) {
    activatedRoute.paramMap.pipe(
      switchMap(paramMap => this.getRmaNumberFromParams(paramMap)),
      switchMap(rmaNumber => this.refreshRma(rmaNumber))
    ).subscribe();
  }

  private addReferenceToRma(reference: Reference) {
    this.loading = true;

    this.rmaService.addReferenceToRma(reference.isbn, this.rma.number)
      .subscribe({
        complete: () => this.loading = false,
        error: _ => this.loading = false,
        next: rma => this.rma = rma,
      });
  }

  public cancel() {
    this.rmaService.cancel(this.rma.number)

      .subscribe(() => this.router.navigateByUrl("/inventory/rma"));
  }

  public confirmRma() {
    this.rmaService.confirm(this.rma.number)

      .subscribe(() => this.router.navigateByUrl("/inventory/rma"));
  }

  public onSearchKeyup(event: KeyboardEvent) {
    if (event.key != "Enter") {
      return;
    }

    this.search();
  }

  public openSearchDialog(references: Page<Reference>) {
    let modalRef = this.modalService.open(ReferenceSearchDialogComponent, { size: 'xl' });

    var component = this;
    modalRef.componentInstance.supplier = this.rma.supplier.name;
    modalRef.componentInstance.onAddReferences = (references: Reference[]) => from(references)
      .pipe(
        mergeMap(reference => component.rmaService.addReferenceToRma(reference.isbn, this.rma.number)),
        toArray(),
        switchMap(_ => component.refreshRma(component.rma.number))
      );

    this.referenceService.initSearchResult({ initialResults: references, query: this.query });
  }

  private getRmaNumberFromParams(paramMap: ParamMap): Observable<number> {
    let rmaNumber = +(paramMap.get("rma") || "0");
    if (rmaNumber == 0) {
      this.router.navigateByUrl("/404");

      return EMPTY;
    }

    return of(rmaNumber);
  }

  private onSearchResponse(references: Page<Reference>) {
    if (references.numberOfElements == 1 && this.query != "") {
      this.addReferenceToRma(references.content[0]);
    } else if (references.numberOfElements > 1) {
      this.openSearchDialog(references);
    } else {
      this.referenceService.findByIsbn(this.query).subscribe({
        next: reference => this.openUpdateSupplierReferenceModal(reference),
        error: _ => this.openSearchDialog(references),
      })
    }
  }

  private openUpdateSupplierReferenceModal(reference: Reference) {
    this.query = "";
    var modalRef = this.modalService.open(UpdateSupplierReferenceDialogComponent);


    modalRef.componentInstance.reference = reference;
    modalRef.componentInstance.supplier = this.rma.supplier;
    for (let supplierReference of reference.supplierReferences) {
      if (supplierReference.active) {
        modalRef.componentInstance.supplierReference = supplierReference;
      }
    }
    modalRef.componentInstance.onSupplierReferenceUpdate = (reference: Reference) => this.addReferenceToRma(reference);
  }

  private refreshRma(rma: number): Observable<Rma> {
    return this.rmaService.findByNumber(rma)
      .pipe(tap(rma => this.rma = rma));
  }

  public removeDetail(detail: RmaDetail) {
    this.rmaService.removeDetail(this.rma.number, detail.reference.isbn)

      .pipe(switchMap(() => this.refreshRma(this.rma.number)))

      .subscribe();
  }

  public search() {
    this.searchInProgress = true;
    this.loading = true;

    this.referenceService.search({
      query: this.query,
      author: [],
      genre: [],
      publisher: [],
      supplier: [this.rma.supplier.name],
    }, 0, { imageMaxWidth: 35 }).subscribe({
      complete: () => {
        this.searchInProgress = false;
        this.loading = false;
      },
      error: () => {
        this.searchInProgress = false;
        this.loading = false;
      },
      next: references => this.onSearchResponse(references)
    });
  }

  public updateQuantity(detail: RmaDetail) {
    this.rmaService.updateQuantity(detail.quantity, detail.reference.isbn, this.rma.number,)

      .subscribe();
  }
}